/* PaywallSheet — bottom drawer that opens Razorpay Checkout for ₹49/month.
 *
 * Loads the Razorpay JS SDK on demand, calls /api/billing/create-order,
 * opens Checkout, then verifies on success and refreshes the user state.
 */
import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Sparkle, Check, X, ShieldCheck, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RAZORPAY_SDK = "https://checkout.razorpay.com/v1/checkout.js";

let _sdkLoaded = false;
function loadRazorpaySdk() {
  if (_sdkLoaded || (typeof window !== "undefined" && window.Razorpay)) {
    _sdkLoaded = true;
    return Promise.resolve(true);
  }
  return new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = RAZORPAY_SDK;
    s.async = true;
    s.onload = () => {
      _sdkLoaded = true;
      resolve(true);
    };
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const FEATURES = [
  { Icon: Lightning, title: "Hit-Rate verification", body: "See exactly how accurate the engine has been for your area" },
  { Icon: Sparkle, title: "Full Pulse + Report", body: "Atmospheric panels, METAR, sounding, 7-day outlook" },
  { Icon: ShieldCheck, title: "Storm-watch alerts", body: "Push notifications when severe weather is loaded for your city" },
];

export default function PaywallSheet({ open, onClose, theme }) {
  const { user, refresh } = useAuth();
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(false);
  const dark = theme?.id === "night" || theme?.id === "storm";

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/billing/config`).then((r) => setConfig(r.data)).catch(() => setConfig(null));
  }, [open]);

  const startPayment = useCallback(async (mode = "subscription") => {
    if (busy) return;
    setBusy(true);
    try {
      const ok = await loadRazorpaySdk();
      if (!ok || !window.Razorpay) {
        toast.error("Couldn't load the payment library. Please retry.");
        return;
      }

      // Auto-renewing subscription (default) — Razorpay sends a UPI mandate
      // / card-recurring auth, then auto-charges every 30 days. Webhook
      // credits the days. Subscriptions only fire if `subscriptions_enabled`.
      if (mode === "subscription" && config?.subscriptions_enabled) {
        const subRes = await axios.post(
          `${API}/billing/create-subscription`,
          {},
          { withCredentials: true },
        );
        const sub = subRes.data;
        const rz = new window.Razorpay({
          key: sub.key_id,
          subscription_id: sub.subscription_id,
          name: "Nowcast Pro",
          description: "Auto-renewing · ₹49 / month",
          prefill: sub.prefill,
          theme: { color: "#f59e0b" },
          modal: { ondismiss: () => setBusy(false) },
          handler: async () => {
            await refresh();
            toast.success("You're on auto-renew. First charge after the trial ends 🎉");
            onClose?.();
            setBusy(false);
          },
        });
        rz.on("payment.failed", () => {
          toast.error("Payment failed. Please try a different method.");
          setBusy(false);
        });
        rz.open();
        return;
      }

      // Fallback / manual top-up — single ₹49 charge, grants 30 days, no auto-renew.
      const orderRes = await axios.post(
        `${API}/billing/create-order`,
        {},
        { withCredentials: true },
      );
      const order = orderRes.data;

      const rz = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "Nowcast Pro",
        description: "1 month · hyperlocal rain intelligence",
        prefill: order.prefill,
        theme: { color: "#f59e0b" },
        modal: {
          ondismiss: () => setBusy(false),
        },
        handler: async (resp) => {
          try {
            await axios.post(
              `${API}/billing/verify-payment`,
              {
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              },
              { withCredentials: true },
            );
            await refresh();
            toast.success("Welcome to Nowcast Pro 🚀");
            onClose?.();
          } catch (e) {
            toast.error(
              e?.response?.data?.detail ||
                "Payment captured but verification failed. We'll fix this — please contact support if Pro doesn't activate within 5 minutes.",
            );
          } finally {
            setBusy(false);
          }
        },
      });
      rz.on("payment.failed", () => {
        toast.error("Payment failed. Please try a different method.");
        setBusy(false);
      });
      rz.open();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Couldn't start checkout. Please retry.",
      );
      setBusy(false);
    }
  }, [busy, config, onClose, refresh]);

  const amount = config?.amount_inr ?? 49;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-end justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          data-testid="paywall-sheet"
        >
          <motion.div
            className="absolute inset-0"
            style={{ background: "rgba(15,23,42,0.55)" }}
            onClick={busy ? undefined : onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            className="relative w-full sm:max-w-[420px] rounded-t-[28px] px-6 pb-8 pt-5"
            style={{
              background: dark ? "#0f172a" : "#fffbeb",
              border: `1px solid ${theme?.surfaceBorder || "rgba(15,23,42,0.08)"}`,
              boxShadow: "0 -20px 60px rgba(15,23,42,0.35)",
              paddingBottom: "calc(env(safe-area-inset-bottom) + 28px)",
            }}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 24, stiffness: 240 }}
          >
            <div className="flex items-center justify-between mb-5 relative">
              <div
                className="w-10 h-1 rounded-full mx-auto"
                style={{ background: dark ? "rgba(255,255,255,0.18)" : "rgba(15,23,42,0.18)" }}
              />
              <button
                type="button"
                onClick={busy ? undefined : onClose}
                disabled={busy}
                aria-label="Close paywall"
                data-testid="paywall-close"
                className="absolute top-0 right-0 w-8 h-8 rounded-full flex items-center justify-center"
                style={{
                  background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
                  color: theme?.textSecondary || "#475569",
                }}
              >
                <X size={12} weight="bold" />
              </button>
            </div>

            <div
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full mb-3"
              style={{
                background: "rgba(245,158,11,0.14)",
                color: "#b45309",
                border: "1px solid rgba(245,158,11,0.3)",
              }}
            >
              <Sparkle size={11} weight="fill" />
              <span className="text-[10px] font-semibold uppercase tracking-[0.16em]">
                Nowcast Pro
              </span>
            </div>

            <div className="flex items-baseline gap-1.5">
              <span
                className="font-display tabular-nums"
                style={{
                  fontSize: 44,
                  fontWeight: 700,
                  letterSpacing: "-0.03em",
                  color: theme?.textPrimary || "#0f172a",
                }}
              >
                ₹{amount}
              </span>
              <span
                className="text-[13px] font-medium"
                style={{ color: theme?.textSecondary || "#475569" }}
              >
                / month
              </span>
            </div>
            <p
              className="mt-1 text-[12px]"
              style={{ color: theme?.textMuted || "#94a3b8" }}
            >
              {user?.is_pro
                ? "Top up your plan — adds 30 days from your current expiry."
                : "Try Pro free for 7 days. ₹49/month after — cancel anytime."}
            </p>

            <ul className="mt-5 space-y-3">
              {FEATURES.map((f, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span
                    className="shrink-0 w-7 h-7 rounded-xl flex items-center justify-center"
                    style={{
                      background: "rgba(245,158,11,0.16)",
                      color: "#b45309",
                    }}
                  >
                    <f.Icon size={14} weight="duotone" />
                  </span>
                  <div className="flex-1">
                    <div
                      className="text-[13px] font-semibold leading-tight"
                      style={{ color: theme?.textPrimary || "#0f172a" }}
                    >
                      {f.title}
                    </div>
                    <div
                      className="text-[11px] leading-snug mt-0.5"
                      style={{ color: theme?.textMuted || "#94a3b8" }}
                    >
                      {f.body}
                    </div>
                  </div>
                  <Check size={14} weight="bold" style={{ color: "#10b981" }} />
                </li>
              ))}
            </ul>

            <button
              type="button"
              onClick={() => startPayment("subscription")}
              disabled={busy}
              data-testid="paywall-pay"
              className="mt-6 w-full py-3.5 rounded-2xl font-semibold transition-transform active:scale-[0.985] disabled:opacity-70"
              style={{
                background: "linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)",
                color: "#0f172a",
                boxShadow: "0 12px 28px rgba(245,158,11,0.42)",
              }}
            >
              {busy
                ? "Opening Razorpay…"
                : config?.subscriptions_enabled
                  ? `Auto-renew · ₹${amount} / month`
                  : `Pay ₹${amount} · unlock Pro`}
            </button>
            {config?.subscriptions_enabled && (
              <button
                type="button"
                onClick={() => startPayment("one_time")}
                disabled={busy}
                data-testid="paywall-pay-onetime"
                className="mt-2 w-full py-2 text-[12px] font-medium underline-offset-2 hover:underline"
                style={{ color: theme?.textMuted || "#94a3b8" }}
              >
                Or pay ₹{amount} once (no auto-renew)
              </button>
            )}

            <p
              className="mt-3 text-center text-[10px]"
              style={{ color: theme?.textMuted || "#94a3b8" }}
            >
              Powered by Razorpay · UPI · Cards · Netbanking · Wallets · Cancel anytime
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
